import { type FC, type ReactNode, useCallback, useEffect, useState } from 'react';

import type { ConfigKey, MapConfigState } from '@/types';

import { MapConfigContext } from '@/contexts/map-config/map-config-context';
import getCookieToken from '@/hooks/get-cookie-token';
import useFetchMapConfig from '@/hooks/use-fetch-map-config';
import useResetMapConfig from '@/hooks/use-reset-map-config';
import useUpdateMapConfig from '@/hooks/use-update-map-config';
import { INITIAL_MAP_CONFIG } from '@/utils/constants/map';

interface MapConfigProviderProps {
  children: ReactNode;
}

export const MapConfigProvider: FC<MapConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<MapConfigState>(INITIAL_MAP_CONFIG);

  const token = getCookieToken();

  const { data: serverConfig, isLoading } = useFetchMapConfig({
    enabled: !!token,
  });

  const { mutateAsync: updateConfig } = useUpdateMapConfig();
  const { mutateAsync: resetConfig } = useResetMapConfig();
  useEffect(() => {
    if (serverConfig) {
      setConfig(serverConfig);
    }
  }, [serverConfig]);

  const handleToggle = useCallback(
    async (key: ConfigKey) => {
      const newValue = !config[key];

      setConfig((prev) => ({ ...prev, [key]: newValue }));

      try {
        await updateConfig({ key, value: newValue });
      } catch (error) {
        console.error('Failed to update map configuration', error);
        setConfig((prev) => ({ ...prev, [key]: !newValue }));
      }
    },
    [config, updateConfig]
  );

  const handleReset = useCallback(async () => {
    const prevConfig = { ...config };

    const nextConfig: MapConfigState = {
      ...INITIAL_MAP_CONFIG,
      hideDetails: prevConfig.hideDetails,
    };

    setConfig(nextConfig);

    try {
      const apiPayload = {
        ...INITIAL_MAP_CONFIG,
        hideDetails: prevConfig.hideDetails ?? false,
      } as MapConfigState;

      await resetConfig(apiPayload);
    } catch (error) {
      console.error('Failed to reset map configuration', error);
      setConfig(prevConfig);
    }
  }, [config, resetConfig]);

  return (
    <MapConfigContext.Provider value={{ config, isLoading, handleToggle, handleReset }}>
      {children}
    </MapConfigContext.Provider>
  );
};
